import React, { Suspense, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Skeleton, Notification } from '@telus-uds/components-web';
import { loadRemote } from '@module-federation/enhanced/runtime';
import useAuth from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { userFromJwt } from '../lib/userFromJwt';
import { MfeConfig } from '../types/mfe';
import { components } from './components';
import { RemoteUnavailable } from './fallbackPlugin';
import { isLegacyReactRemote } from './legacyReact';
import { LegacyReactIsland } from './LegacyReactIsland';
import { registerMfeRemote } from './remotes';

type Props = Record<string, unknown>;

export interface MfePageOptions {
  /** Load this remote instead of the first config registered under the component name. */
  config?: MfeConfig;
  /**
   * Loads a provider to wrap the page in. It renders in the page's own React, so a
   * provider from a legacy remote wraps a legacy page inside its island.
   */
  loadProvider?: () => Promise<React.ComponentType<any> | undefined>;
}

/** Props for the provider from MfePageOptions.loadProvider; never passed to the remote. */
export const PROVIDER_PROPS = 'providerProps';

export const generateMfePage = (componentName: string, options: MfePageOptions = {}) => {
  const config = options.config ?? components.find(mfe => mfe.name === componentName);

  const LazyMfe = React.lazy<React.ComponentType<any>>(async () => {
    try {
      registerMfeRemote(config!);
      const [module, Provider]: [any, React.ComponentType<any> | undefined] = await Promise.all([
        loadRemote(`${config!.remote}/${config!.name}`),
        options.loadProvider?.(),
      ]);

      if (!module || (!module.default && !module[config!.name])) {
        return {
          default: () => (
            <Notification variant={{ style: 'error' }}>
              Failed to load: {config!.name}
            </Notification>
          ),
        };
      }

      const component = module.default ?? module[config!.name];
      if (component === RemoteUnavailable) {
        return { default: component };
      }
      if (isLegacyReactRemote(config!.remote)) {
        return {
          default: ({ [PROVIDER_PROPS]: providerProps, ...props }: Props) => (
            <LegacyReactIsland
              name={config!.name}
              component={component}
              props={props}
              provider={Provider && { component: Provider, props: (providerProps ?? {}) as Props }}
            />
          ),
        };
      }
      if (!Provider) {
        return { default: component };
      }
      const Component = component;
      return {
        default: ({ [PROVIDER_PROPS]: providerProps, ...props }: Props) => (
          <Provider {...(providerProps as Props)}>
            <Component {...props} />
          </Provider>
        ),
      };
    } catch (error: any) {
      return {
        default: () => (
          <Notification variant={{ style: 'error' }}>
            Error loading {config!.name}: {error.message}
          </Notification>
        ),
      };
    }
  });

  const MfePage: (extraProps: Props) => React.JSX.Element = (extraProps = {}) => {
    const authState = useAuth();
    const { language } = useLanguage();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const user = useMemo(() => userFromJwt(authState.jwt), [authState.jwt]);

    if (authState.isLoading) return <Skeleton />;

    if (authState.error) {
      return (
        <Notification variant={{ style: 'error' }}>
          Auth error: {authState.error}
        </Notification>
      );
    }

    return (
      <Suspense fallback={<Skeleton />}>
        <LazyMfe
          jwt={authState.jwt}
          userEntityRef={user?.userEntityRef}
          email={user?.email}
          lang={language}
          routeParams={params}
          location={location}
          navigate={navigate}
          {...extraProps}
        />
      </Suspense>
    );
  };

  return MfePage;
};
